import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createConsumer } from '@rails/actioncable';
import { Head } from '@inertiajs/react';
import { Globe, Loader2, Play } from 'lucide-react';
import { PlatformConsoleBreadcrumb } from '@/components/platform_console/PlatformConsoleBreadcrumb';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

type MunicipalityOption = {
  id: number;
  name: string;
};

type WebScrapeMode = 'new' | 'append';

type WebScrapeDocumentNewProps = {
  municipalities: MunicipalityOption[];
  errors?: Record<string, string[] | string>;
};

type LogEntry = {
  timestamp: number;
  jobShort: string;
  line: string;
};

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

function fmtTime(seconds: number): string {
  return new Date(seconds * 1000).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
}

export default function WebScrapeDocumentNew({ municipalities = [], errors = {} }: WebScrapeDocumentNewProps) {
  const [mode, setMode] = useState<WebScrapeMode>(() => {
    if (typeof window === 'undefined') return 'new';
    const documentIdParam = new URLSearchParams(window.location.search).get('document_id');
    return documentIdParam && documentIdParam.trim().length > 0 ? 'append' : 'new';
  });
  const [documentName, setDocumentName] = useState('');
  const [municipalityId, setMunicipalityId] = useState('');
  const [existingDocumentId, setExistingDocumentId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('document_id')?.trim() ?? '';
  });
  const [urlsText, setUrlsText] = useState('');

  const [returnPageMarkdown, setReturnPageMarkdown] = useState(true);
  const [chunkSize, setChunkSize] = useState('3000');
  const [chunkOverlap, setChunkOverlap] = useState('200');
  const [deduplicate, setDeduplicate] = useState(true);
  const [concurrency, setConcurrency] = useState('5');
  const [premiumProxy, setPremiumProxy] = useState(false);

  const [isRunning, setIsRunning] = useState(false);
  const [rootJobId, setRootJobId] = useState<string | null>(null);
  const [pollDocumentId, setPollDocumentId] = useState<number | null>(null);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [requestError, setRequestError] = useState<string | null>(null);

  const subsRef = useRef<Map<string, any>>(new Map());
  const cableRef = useRef<any>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  const teardownCableSubs = useCallback(() => {
    const cable = (typeof window !== 'undefined' ? (window as any).App?.cable : null) ?? cableRef.current ?? createConsumer();
    cableRef.current = cable;
    subsRef.current.forEach(sub => cable.subscriptions.remove(sub));
    subsRef.current.clear();
  }, []);

  /** Subscribe once per Sidekiq jid so import + vectorization streams merge into one log. */
  const ensureJobSubscription = useCallback(
    (jobId: string) => {
      if (!jobId || subsRef.current.has(jobId)) {
        return;
      }

      const cable = (window as any).App?.cable ?? createConsumer();
      cableRef.current = cable;

      const sub = cable.subscriptions.create(
        { channel: 'JobProgressChannel', job_id: jobId },
        {
          received: (data: Record<string, unknown>) => {
            const message = String(data.message ?? '');
            const timestamp = typeof data.timestamp === 'number' ? Number(data.timestamp) : Math.floor(Date.now() / 1000);
            setLogEntries(prev => [
              ...prev,
              {
                timestamp,
                jobShort: jobId.length >= 8 ? jobId.slice(0, 8) : jobId,
                line: message
              }
            ]);

            const vectorizeJobId = data.vectorize_job_id;
            if (typeof vectorizeJobId === 'string' && vectorizeJobId.length > 0) {
              ensureJobSubscription(vectorizeJobId);
            }

            if (message.includes('ERROR:')) {
              setIsRunning(false);
              return;
            }

            const vectorizationFinished =
              message.includes('Vectorization completed!') ||
              message.includes('No chunks need vectorization');

            if (vectorizationFinished) {
              setIsRunning(false);
            }
          }
        }
      );

      subsRef.current.set(jobId, sub);
    },
    []
  );

  useEffect(() => () => teardownCableSubs(), [teardownCableSubs]);

  useEffect(() => {
    if (!pollDocumentId || !isRunning) {
      return;
    }

    const fetchStatus = async () => {
      try {
        const res = await fetch(`/documents/web_scrape/status/${pollDocumentId}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!res.ok) {
          return;
        }

        const s = await res.json();
        const docStatus =
          typeof s.status === 'string' || typeof s.status === 'number' ? String(s.status) : '';

        const done =
          docStatus === 'completed' ||
          docStatus === 'failed';

        if (done) {
          setIsRunning(false);
        }
      } catch {
        /* polling is best-effort */
      }
    };

    fetchStatus();
    const interval = window.setInterval(fetchStatus, 2000);
    return () => window.clearInterval(interval);
  }, [pollDocumentId, isRunning]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ block: 'end' });
  }, [logEntries]);

  const normalizedUrls = useMemo(() => {
    return urlsText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  }, [urlsText]);

  const parsedExistingDocumentId = useMemo(() => {
    const parsed = Number.parseInt(existingDocumentId.trim(), 10);
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
  }, [existingDocumentId]);

  const canRun = useMemo(() => {
    if (isRunning) return false;
    if (normalizedUrls.length === 0) return false;
    if (mode === 'append') {
      return parsedExistingDocumentId !== null;
    }
    if (!documentName.trim()) return false;
    if (!municipalityId) return false;
    return true;
  }, [isRunning, mode, documentName, municipalityId, normalizedUrls.length, parsedExistingDocumentId]);

  const startRun = async () => {
    setRequestError(null);
    teardownCableSubs();
    setLogEntries([]);
    setRootJobId(null);
    setPollDocumentId(null);
    setIsRunning(true);

    try {
      const scrapingOptions = {
        return_page_markdown: returnPageMarkdown,
        chunk_size: Number(chunkSize),
        chunk_overlap: Number(chunkOverlap),
        deduplicate,
        concurrency: Number(concurrency),
        premium_proxy: premiumProxy
      };

      const response =
        mode === 'append' && parsedExistingDocumentId !== null
          ? await fetch(`/documents/web_scrape/append/${parsedExistingDocumentId}.json`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrfToken(),
                Accept: 'application/json'
              },
              body: JSON.stringify({
                urls: normalizedUrls,
                scraping_options: scrapingOptions
              })
            })
          : await fetch('/documents/web_scrape.json', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrfToken(),
                Accept: 'application/json'
              },
              body: JSON.stringify({
                name: documentName.trim(),
                municipality_id: municipalityId,
                urls: normalizedUrls,
                scraping_options: scrapingOptions
              })
            });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = `HTTP ${response.status}`;
        if (typeof data?.errors_message === 'string') {
          msg = data.errors_message;
        } else if (Array.isArray(data?.errors)) {
          msg = data.errors
            .map((e: unknown) => {
              if (typeof e === 'object' && e !== null && 'message' in e) {
                return String((e as { message?: string }).message ?? JSON.stringify(e));
              }
              return String(e);
            })
            .join(', ');
        } else if (data?.errors) {
          msg = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
        }
        setRequestError(msg);
        setIsRunning(false);
        return;
      }

      const newJobId = data?.job_id as string | undefined;
      if (!newJobId) {
        setRequestError('Job started but the response was missing job_id.');
        setIsRunning(false);
        return;
      }

      setRootJobId(newJobId);

      const documentIdRaw = data?.document_id;
      const documentIdParsed =
        typeof documentIdRaw === 'number' ? documentIdRaw : Number.parseInt(String(documentIdRaw), 10);
      if (!Number.isNaN(documentIdParsed)) {
        setPollDocumentId(documentIdParsed);
      }

      const now = Math.floor(Date.now() / 1000);
      setLogEntries([
        {
          timestamp: now,
          jobShort: newJobId.slice(0, 8),
          line: 'Connected to import job progress. ScrapingBee API progress and vectorization will stream here when available.'
        }
      ]);
      ensureJobSubscription(newJobId);
    } catch (e: any) {
      setRequestError(e?.message || 'Failed to start job');
      setIsRunning(false);
    }
  };

  return (
    <>
      <Head title="Web Scrape → Document" />
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          <PlatformConsoleBreadcrumb section="document_pipeline" current="Web Scrape" />
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6" />
            Web Scrape → Document
          </h1>

          <Alert>
            <AlertDescription>
              {mode === 'append'
                ? 'Append URLs to an existing hierarchical document. ScrapingBee progress, chunk import, and vectorization stream in the live log below.'
                : 'Enter a list of URLs to scrape into chunks. This creates a Document, associates it to the municipality, streams ScrapingBee API progress, then imports chunks and follows the vectorization job in the same live log.'}
            </AlertDescription>
          </Alert>

          {requestError && (
            <Alert variant="destructive">
              <AlertDescription>{requestError}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Document + Scraping Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Mode</Label>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="web_scrape_mode"
                      checked={mode === 'new'}
                      onChange={() => setMode('new')}
                    />
                    New document
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="web_scrape_mode"
                      checked={mode === 'append'}
                      onChange={() => setMode('append')}
                    />
                    Append to existing document
                  </label>
                </div>
              </div>

              {mode === 'append' ? (
                <div className="space-y-2">
                  <Label htmlFor="existing_document_id">Existing document ID</Label>
                  <Input
                    id="existing_document_id"
                    value={existingDocumentId}
                    onChange={e => setExistingDocumentId(e.target.value)}
                    placeholder="123"
                    inputMode="numeric"
                  />
                  <p className="text-xs text-gray-600">
                    Deep link: add <span className="font-mono">?document_id=123</span> to pre-fill append mode.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="document_name">Document name</Label>
                    <Input
                      id="document_name"
                      value={documentName}
                      onChange={e => setDocumentName(e.target.value)}
                      placeholder="Example: Sallisaw City Websites (May 2026)"
                    />
                    {errors?.name && (
                      <div className="text-sm text-red-600">
                        {Array.isArray(errors.name) ? errors.name.join(', ') : errors.name}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Municipality</Label>
                    <Select
                      value={municipalityId || undefined}
                      onValueChange={setMunicipalityId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a municipality" />
                      </SelectTrigger>
                      <SelectContent>
                        {municipalities.map(m => (
                          <SelectItem key={m.id} value={String(m.id)}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="urls">URLs (one per line)</Label>
                <Textarea
                  id="urls"
                  rows={10}
                  value={urlsText}
                  onChange={e => setUrlsText(e.target.value)}
                  placeholder={
                    'https://example.gov/page\nhttp://example.gov/legacy-page'
                  }
                />
                <div className="text-xs text-gray-600">
                  {normalizedUrls.length} URL(s). HTTP and HTTPS are supported for
                  public hosts (use HTTP for legacy sites without TLS).
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="chunk_size">Chunk size</Label>
                  <Input id="chunk_size" value={chunkSize} onChange={e => setChunkSize(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chunk_overlap">Chunk overlap</Label>
                  <Input id="chunk_overlap" value={chunkOverlap} onChange={e => setChunkOverlap(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="concurrency">Concurrency</Label>
                  <Input id="concurrency" value={concurrency} onChange={e => setConcurrency(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Flags</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={returnPageMarkdown} onChange={e => setReturnPageMarkdown(e.target.checked)} />
                      return_page_markdown
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={deduplicate} onChange={e => setDeduplicate(e.target.checked)} />
                      deduplicate
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={premiumProxy} onChange={e => setPremiumProxy(e.target.checked)} />
                      premium_proxy
                    </label>
                  </div>
                  <p className="text-xs text-gray-600">
                    Enable premium_proxy for sites that block default ScrapingBee IPs (higher credit cost).
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={startRun} disabled={!canRun} className="gap-2">
                  {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {mode === 'append' ? 'Append URLs + import + vectorize' : 'Run scrape + import + vectorize'}
                </Button>
                {rootJobId && (
                  <div className="text-sm text-gray-600">
                    Import jid: <span className="font-mono">{rootJobId}</span>
                  </div>
                )}
              </div>

              {(logEntries.length > 0 || isRunning) && (
                <div className="space-y-2">
                  <Label>Live output</Label>
                  <div className="max-h-[28rem] overflow-y-auto rounded-md border bg-zinc-950 p-3 font-mono text-xs text-zinc-100">
                    {logEntries.map((entry, i) => (
                      <div key={`${entry.timestamp}-${i}`} className="whitespace-pre-wrap break-words">
                        <span className="text-zinc-500">{fmtTime(entry.timestamp)} </span>
                        <span className="text-teal-400">[{entry.jobShort}] </span>
                        <span>{entry.line}</span>
                      </div>
                    ))}
                    {isRunning && (
                      <div className="mt-2 flex items-center gap-2 text-zinc-500">
                        <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                        Waiting for updates…
                      </div>
                    )}
                    <div ref={logEndRef} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
